import { UseFormSetValue } from 'react-hook-form';

import { fetchModelsByRangeId } from '@/app/sell-my-car/service';
import {
  FUEL_TYPES,
  TRANSMISSION_TYPES,
} from '@/constants/sell-your-car/sell-your-car.constants';

import {
  SellYourFormValues,
  TransformedBrand,
  TransformedModel,
} from '../service';

export type BrandData = TransformedBrand;
export type ModelData = TransformedModel;

export async function processVehicleMakeSelection(
  value: { value: string; label?: string },
  treeData: TransformedBrand[],
  setValue: UseFormSetValue<SellYourFormValues>,
): Promise<{ models: TransformedModel[]; rangeId: number | null }> {
  try {
    if (!value || !value.value) {
      setValue('vehicle_brand', '');
      setValue('vehicle_range', '');
      setValue('vehicle_model', '');
      return { models: [], rangeId: null };
    }

    const [brandName, rangeName, rangeId] = value.value.split('|');

    if (!brandName || !rangeName || !rangeId) {
      setValue('vehicle_brand', '');
      setValue('vehicle_range', '');
      setValue('vehicle_model', '');
      return { models: [], rangeId: null };
    }

    const brand = treeData.find((b) => b.title === brandName);

    if (!brand) {
      return { models: [], rangeId: null };
    }

    const range = brand.children.find((r) => r.rangeId === Number(rangeId));

    if (!range) {
      return { models: [], rangeId: null };
    }

    setValue('vehicle_brand', brandName);
    setValue('vehicle_range', rangeName);
    setValue('vehicle_model', '');

    const modelData = await fetchModelsByRangeId(range.rangeId);

    return { models: modelData, rangeId: range.rangeId };
  } catch (error) {
    console.error('Error processing vehicle make selection:', error);
    return { models: [], rangeId: null };
  }
}

export function processModelSelection(
  value: string,
  models: TransformedModel[],
  setValue?: UseFormSetValue<SellYourFormValues>,
) {
  if (!setValue) return;

  const selectedModel = models.find((model) => model.value === value);

  if (selectedModel) {
    if (selectedModel['fuel-type']) {
      const fuelType = normalizeFuelType(selectedModel['fuel-type']);

      if (fuelType) {
        setValue('vehicle_fuel', fuelType);
      }
    }

    if (selectedModel.transmission) {
      const transmission = normalizeTransmission(selectedModel.transmission);

      if (transmission) {
        setValue('vehicle_transmission', transmission);
      }
    }
  }
}

function normalizeFuelType(fuelType: string): string | undefined {
  const lowerCaseFuel = fuelType.toLowerCase().trim();

  if (FUEL_TYPES.includes(fuelType)) {
    return fuelType;
  }

  if (lowerCaseFuel.includes('petrol')) return 'Petrol';
  if (lowerCaseFuel.includes('diesel')) return 'Diesel';
  if (lowerCaseFuel.includes('hybrid')) return 'Hybrid';
  if (lowerCaseFuel.includes('electric')) return 'Electric';

  return undefined;
}

function normalizeTransmission(transmission: string): string | undefined {
  const lowerCaseTransmission = transmission.toLowerCase().trim();

  if (TRANSMISSION_TYPES.includes(transmission)) {
    return transmission;
  }

  if (lowerCaseTransmission.includes('auto')) return 'Automatic';
  if (lowerCaseTransmission.includes('manual')) return 'Manual';

  return undefined;
}
